import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { MediaSummary, StructuredFilter } from "@media-viewer/shared";
import { MediaTile } from "../components/MediaTile";
import { useSelection } from "../components/SelectionContext";
import type { QuerySnapshot } from "../components/SelectionContext";
import { SelectionToolbar, SelectModeToggle } from "../components/SelectionToolbar";
import { SearchBar } from "../components/SearchBar";
import { filterFromUrlState, filterToUrlState } from "../search/filter-to-params";
import { buildPlayHref } from "../util/detail-url";

interface MediaGridProps {
  kind?: "photo" | "video";
  tag?: string;
  set: string;
  isAdmin: boolean;
  emptyHint?: string;
}

type SortOrder = "newest" | "oldest" | "added_new" | "added_old" | "random" | "favorited_at";
const SORT_LABELS: Record<SortOrder, string> = {
  newest: "Newest taken",
  oldest: "Oldest taken",
  added_new: "Recently added",
  added_old: "Oldest added",
  random: "Random shuffle",
  favorited_at: "Recently favorited",
};
const SORT_KEY = "mv-grid-sort";
function isSortOrder(v: string | null): v is SortOrder {
  return (
    v === "newest" ||
    v === "oldest" ||
    v === "added_new" ||
    v === "added_old" ||
    v === "random" ||
    v === "favorited_at"
  );
}
/**
 * URL params win over localStorage so "back" from a media detail view
 * restores the same refined listing the user was on. localStorage is the
 * fallback when no URL param is present (fresh navigation to /videos,
 * say). The final fallback is "newest".
 */
function initialSort(set: string): SortOrder {
  try {
    const url = new URLSearchParams(window.location.search).get("order");
    if (isSortOrder(url)) return url;
    const ls = window.localStorage.getItem(SORT_KEY);
    if (isSortOrder(ls)) return ls;
  } catch {
    /* ignore */
  }
  return set === "favorites" ? "favorited_at" : "newest";
}

function initialFilter(): StructuredFilter | null {
  try {
    const param = new URLSearchParams(window.location.search).get("filter");
    return filterFromUrlState(param);
  } catch {
    return null;
  }
}

export function MediaGrid({ kind, tag, set, isAdmin, emptyHint }: MediaGridProps) {
  const sel = useSelection();
  const [filter, setFilterState] = useState<StructuredFilter | null>(initialFilter);
  const [order, setOrder] = useState<SortOrder>(() => initialSort(set));
  useEffect(() => {
    try {
      window.localStorage.setItem(SORT_KEY, order);
    } catch {
      /* ignore */
    }
  }, [order]);

  // Persist filter into the URL so back-button and deep-links work.
  const setFilter = (next: StructuredFilter | null) => {
    setFilterState(next);
    try {
      const u = new URL(window.location.href);
      const encoded = filterToUrlState(next);
      if (encoded.filter) {
        u.searchParams.set("filter", encoded.filter);
      } else {
        u.searchParams.delete("filter");
      }
      window.history.replaceState(null, "", u.toString());
    } catch {
      /* ignore */
    }
  };

  // Stable seed for random mode. The server pages off md5(id||seed) so
  // pagination is consistent as long as the seed doesn't change. We
  // generate one when the user picks "random" and bump it when they
  // click "Shuffle again". Cleared when switching to a non-random sort.
  const [randomSeed, setRandomSeed] = useState<string | null>(() =>
    initialSort(set) === "random" ? String(Date.now()) : null,
  );
  useEffect(() => {
    if (order === "random" && !randomSeed) setRandomSeed(String(Date.now()));
    if (order !== "random" && randomSeed) setRandomSeed(null);
  }, [order, randomSeed]);

  const isFavorites = set === "favorites";
  const q = useInfiniteQuery({
    queryKey: [
      "media",
      "list",
      {
        kind,
        tag,
        set: isFavorites ? "favorites" : undefined,
        filter,
        order,
        seed: randomSeed,
      },
    ],
    queryFn: ({ pageParam }) =>
      api.listMedia({
        kind,
        tag,
        set: isFavorites ? "favorites" : undefined,
        before: pageParam,
        limit: 60,
        order,
        seed: randomSeed ?? undefined,
        structured: filter,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  // Total count (no filters) so the page can show "of N total" beside the
  // header. Independent of search/filter so it reflects the category as a whole.
  const total = useQuery({
    queryKey: ["media", "total", { kind, tag, set: isFavorites ? "favorites" : undefined }],
    queryFn: () => api.countMedia({ kind, tag, set: isFavorites ? "favorites" : undefined }),
    staleTime: 30_000,
  });

  const filterHasFilter = filter != null;

  const matchCount = useQuery({
    queryKey: [
      "media",
      "count",
      {
        kind,
        tag,
        set: isFavorites ? "favorites" : undefined,
        filter,
      },
    ],
    queryFn: () =>
      api.countMedia({
        kind,
        tag,
        set: isFavorites ? "favorites" : undefined,
        structured: filter,
      }),
    enabled: filterHasFilter,
  });

  // Snapshot for SelectionToolbar's by-query bulk actions. Carries the page
  // scope plus the whole structured filter, so "select all matching" resolves
  // the identical set the grid lists/counts (same OR-union over fuzzy tags) —
  // no flattening to a lossy tag CSV that would AND the matched tags together.
  const snapshot: QuerySnapshot = {
    kind,
    tag,
    structured: filter,
  };

  const items: MediaSummary[] = q.data?.pages.flatMap((p) => p.items) ?? [];

  useEffect(() => {
    sel.setVisibleIds(items.map((i) => i.id));
  }, [items, sel]);

  // Any change to filter inputs while a selection is active drops the
  // selection — query-mode selections in particular must never silently
  // operate on a different matched set than the user clicked.
  useEffect(() => {
    if (!sel.active) return;
    sel.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, kind, tag, order]);

  // Infinite scroll. Uses an IntersectionObserver as the primary trigger
  // and a scroll-listener safety net (some layouts don't reliably fire
  // intersection events when the sentinel first becomes visible after a
  // layout shift). Both call the same guarded fetch.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!q.hasNextPage) return;
    let cancelled = false;
    const tryFetch = () => {
      if (cancelled || !q.hasNextPage || q.isFetchingNextPage) return;
      const rect = el.getBoundingClientRect();
      // Trigger when the sentinel is within ~800px of the viewport bottom.
      if (rect.top - window.innerHeight < 800) {
        void q.fetchNextPage();
      }
    };
    const obs = new IntersectionObserver(tryFetch, { rootMargin: "800px 0px" });
    obs.observe(el);
    // Kick once after mount so very short pages auto-extend to fill the
    // viewport without waiting for a scroll event.
    tryFetch();
    const onScroll = () => tryFetch();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelled = true;
      obs.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
    // items.length is in deps so when a fetched page renders we re-arm and
    // immediately check whether the sentinel is still in the trigger zone
    // (chains pages until the user has actually scrolled past).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.hasNextPage, q.isFetchingNextPage, items.length]);

  const noFilters = !filter;
  const firstId = items[0]?.id;

  // Keep the shell mounted across query refetches so the search input
  // doesn't unmount mid-keystroke and steal focus on mobile. Previously a
  // top-level `if (q.isPending) return <Loading/>` blew away the entire
  // tree on every new query key — typing the 4th character of a search
  // would re-trigger isPending, drop the input, and the on-screen keyboard
  // would close. The loading/error/empty states now live inside the body
  // section; the toolbar, header, and search bar render unconditionally.
  return (
    <>
      <SelectionToolbar
        isAdmin={isAdmin}
        query={snapshot}
        matched={matchCount.data?.count}
        hasFilter={filterHasFilter}
      />
      <div className="flex flex-wrap items-baseline gap-3 px-4 pt-4 text-sm text-zinc-400">
        <h2 className="text-lg font-semibold capitalize text-zinc-100">
          {kind ? `${kind}s` : tag ? `Tag: ${tag}` : isFavorites ? "Favorites" : "Library"}
        </h2>
        {total.data && (
          <span className="text-xs text-zinc-500">
            {total.data.count.toLocaleString()} total
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-zinc-400">
            <span className="hidden sm:inline">Sort</span>
            <select
              value={order}
              onChange={(e) => setOrder(e.target.value as SortOrder)}
              className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-200 hover:bg-zinc-700 focus:border-zinc-500 focus:outline-none"
              aria-label="Sort order"
            >
              {(Object.keys(SORT_LABELS) as SortOrder[])
                .filter((o) => o !== "favorited_at" || isFavorites)
                .map((o) => (
                  <option key={o} value={o}>
                    {SORT_LABELS[o]}
                  </option>
                ))}
            </select>
          </label>
          {order === "random" && (
            <button
              onClick={() => setRandomSeed(String(Date.now()))}
              className="rounded bg-zinc-800 px-3 py-1 text-xs text-zinc-200 hover:bg-zinc-700"
              title="Reshuffle the current view with a new random seed"
            >
              🔀 Shuffle again
            </button>
          )}
          {firstId != null && (
            <>
              <a
                href={buildPlayHref({ id: firstId, set, order, seed: randomSeed, filter: filter ? filterToUrlState(filter).filter ?? null : null })}
                className="rounded bg-emerald-700 px-3 py-1 text-xs text-emerald-100 hover:bg-emerald-600"
                title="Auto-advance from here — photos slide, videos autoplay"
              >
                ▶ Play all
              </a>
              <a
                href={buildPlayHref({ id: firstId, set, shuffle: true })}
                className="rounded bg-violet-700 px-3 py-1 text-xs text-violet-100 hover:bg-violet-600"
                title="Play random items from this set, endlessly"
              >
                🎲 Random
              </a>
            </>
          )}
        </div>
      </div>
      <SearchBar
        filter={filter}
        setFilter={setFilter}
        pinnedKind={kind}
        pinnedTag={tag}
      />
      <div className="px-4 pt-3">
        <SelectModeToggle />
      </div>
      <div className="p-4">
        {q.isPending ? (
          <div className="p-4 text-zinc-500">Loading…</div>
        ) : q.isError ? (
          <div className="p-4 text-red-300">Error loading media: {String(q.error)}</div>
        ) : items.length === 0 ? (
          <div className="rounded bg-zinc-800/40 px-3 py-2 text-xs text-zinc-400">
            {noFilters
              ? (emptyHint ?? "No media yet. Upload some files or wait for the scanner to index your library.")
              : "No media match this search."}
          </div>
        ) : (
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
          >
            {items.map((item, i) => (
              <MediaTile
                key={item.id}
                item={item}
                setParam={set}
                isAdmin={isAdmin}
                index={i}
                aspect="1 / 1"
                extraDetailParams={{
                  order,
                  ...(randomSeed ? { seed: randomSeed } : {}),
                  ...(filter ? { filter: filterToUrlState(filter).filter ?? "" } : {}),
                }}
              />
            ))}
          </div>
        )}
        {q.hasNextPage && (
          <div
            ref={sentinelRef}
            className="mt-6 flex flex-col items-center justify-center gap-2 py-4 text-xs text-zinc-500"
          >
            <button
              onClick={() => void q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="rounded bg-zinc-800 px-4 py-2 text-sm hover:bg-zinc-700 disabled:opacity-60"
            >
              {q.isFetchingNextPage ? "Loading more…" : "Load more"}
            </button>
            <span className="text-[10px] text-zinc-600">
              auto-loads when you scroll near here
            </span>
          </div>
        )}
      </div>
    </>
  );
}
